import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BeforeAfterComparison from './components/BeforeAfterComparison';
import ConversionProcess from './components/ConversionProcess';
import ROICalculator from './components/ROICalculator';
import CommonConcerns from './components/CommonConcerns';
import TechnicalSpecifications from './components/TechnicalSpecifications';
import ClientTestimonials from './components/ClientTestimonials';
import { useNavigate } from 'react-router-dom';
import logo from '../../../public/assets/images/fibrelogo.png';



const CopperToFiberConversionService = () => {
    const navigate = useNavigate();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const benefits = [
    {
      icon: "Zap",
      title: "Lightning-Fast Speeds",
      description: "Experience up to 100x faster internet speeds with fiber optic technology",
      metric: "Up to 10 Gbps"
    },
    {
      icon: "Shield",
      title: "Ultra-Reliable Connection",
      description: "99.9% uptime with immunity to electromagnetic interference",
      metric: "99.9% Uptime"
    },
    {
      icon: "TrendingDown",
      title: "Lower Operating Costs",
      description: "Reduce maintenance costs by up to 75% compared to copper systems",
      metric: "75% Cost Reduction"
    },
    {
      icon: "Infinity",
      title: "Future-Proof Technology",
      description: "Scalable infrastructure that grows with your business needs",
      metric: "20+ Year Lifespan"
    }
  ];

  const conversionStats = [
    { label: "Successful Conversions", value: "500+", icon: "CheckCircle" },
    { label: "Average Speed Increase", value: "75x", icon: "Zap" },
    { label: "Client Satisfaction", value: "98%", icon: "Heart" },
    { label: "Zero Downtime Projects", value: "85%", icon: "Shield" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-gradient-to-br from-primary/5 via-white to-accent/5">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className=" mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 bg-accent/10 text-accent rounded-full text-sm font-medium mb-6">
                  <Icon name="Zap" size={16} className="mr-2" />
                  Transform Your Business Connectivity
                </div>
                
                <h1 className="text-4xl lg:text-5xl font-bold text-text-primary mb-6 leading-tight">
                  Copper to Fiber
                  <span className="block text-primary">Conversion Service</span>
                </h1>
                
                <p className="text-xl text-text-secondary mb-8 leading-relaxed">
                  Transform your legacy copper infrastructure into a high-performance fiber optic network. 
                  Experience lightning-fast speeds, rock-solid reliability, and future-ready scalability 
                  with minimal business disruption.
                </p>

                {/* <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button
                    variant="default"
                    size="lg"
                    iconName="Calculator"
                    iconPosition="left"
                    className="cta-primary"
                  >
                    Calculate Your ROI
                  </Button>
                  <Button
                    variant="outline"
                    size="lg"
                    iconName="Phone"
                    iconPosition="left"
                    className="trust-glow"
                  >
                    Free Consultation
                  </Button>
                </div> */}

                <div className="flex items-center text-sm text-text-secondary">
                  <Icon name="CheckCircle" size={16} className="mr-2 text-accent" />
                  <span>Free network assessment • Zero-downtime installation • 24/7 support</span>
                </div>
              </div>

              <div className="relative">
                <div className="bg-white rounded-2xl shadow-brand-strong p-8">
                  <div className="text-center mb-6">
                    <h3 className="text-2xl font-bold text-text-primary mb-2">
                      Instant Performance Boost
                    </h3>
                    <p className="text-text-secondary">See the difference fiber makes</p>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    {conversionStats?.map((stat, index) => (
                      <div key={index} className="text-center p-4 bg-surface rounded-lg">
                        <Icon name={stat?.icon} size={24} className="mx-auto mb-2 text-primary" />
                        <div className="text-2xl font-bold text-primary mb-1">{stat?.value}</div>
                        <div className="text-xs text-text-secondary">{stat?.label}</div>
                      </div>
                    ))}
                  </div>
                  
                  <div className="mt-6 p-4 bg-accent/10 rounded-lg text-center">
                    <p className="text-sm text-accent font-medium">
                      Join 500+ businesses that have already transformed their connectivity
                    </p>
                  </div>
                </div>

                {/* Floating Elements */}
                <div className="absolute -top-4 -right-4 w-20 h-20 bg-accent/20 rounded-full animate-pulse"></div>
                <div className="absolute -bottom-4 -left-4 w-16 h-16 bg-primary/20 rounded-full animate-pulse delay-1000"></div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Before/After Comparison */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <BeforeAfterComparison />
        </div>
      </section>
      {/* Key Benefits */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-text-primary mb-4">
              Why Choose Fiber Over Copper?
            </h2>
            <p className="text-text-secondary text-lg max-w-3xl mx-auto">
              Fiber optic technology delivers superior performance, reliability, and value 
              compared to traditional copper infrastructure.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits?.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 shadow-brand-moderate hover:shadow-brand-strong transition-all duration-300 micro-bounce">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
                  <Icon name={benefit?.icon} size={24} className="text-primary" />
                </div>
                <h3 className="text-xl font-semibold text-text-primary mb-3">
                  {benefit?.title}
                </h3>
                <p className="text-text-secondary mb-4">
                  {benefit?.description}
                </p>
                <div className="text-2xl font-bold text-accent">
                  {benefit?.metric}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      {/* Conversion Process */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ConversionProcess />
        </div>
      </section>
      {/* ROI Calculator */}
      {/* <section className="py-16 bg-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ROICalculator />
        </div>
      </section> */}
      {/* Common Concerns */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <CommonConcerns />
        </div>
      </section>
      {/* Technical Specifications */}
      <section className="py-16 bg-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <TechnicalSpecifications />
        </div>
      </section>
      {/* Client Testimonials */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ClientTestimonials />
        </div>
      </section>
      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-primary to-secondary">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-6">
              Ready to Transform Your Business Connectivity?
            </h2>
            <p className="text-xl text-white/90 mb-8">
              Join hundreds of businesses that have already made the switch to fiber. 
              Get your free network assessment and ROI analysis today.
            </p>
            
            {/* <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <Button
                variant="outline"
                size="lg"
                className="bg-white text-primary hover:bg-gray-50 border-white"
                iconName="Calendar"
                iconPosition="left"
              >
                Schedule Free Assessment
              </Button> */}
              <Button
                variant="outline"
                size="lg"
                className="bg-transparent text-white border-white hover:bg-white/10 mb-3"
                iconName="MessageSquare"
                iconPosition="left"
                onClick={() => navigate('/get-quote-consultation')}
              >
                Contact Us
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-white/80 text-sm">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={16} className="mr-2" />
                Free Network Assessment
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircle" size={16} className="mr-2" />
                No Obligation Quote
              </div>
              <div className="flex items-center">
                <Icon name="CheckCircle" size={16} className="mr-2" />
                24/7 Emergency Support
              </div>
           
          </div>
        </div>
      </section>
      {/* Footer */}
      <footer className="bg-slate-900 text-white py-12">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid md:grid-cols-4 gap-8">
                  {/* Company Info */}
                  <div className="space-y-4">
                    <div className="flex items-center space-x-3">
                      <img
                                      src={logo}
                                          alt="Xtreem Fibre Solutions"
                                          width={80}
                                          height={80}
                                          className="h-16 w-auto brightness-0 invert transition-transform duration-300 group-hover:scale-105"/>
                                      
                      <div>
                        <h3 className="text-lg font-bold">Xtreem Fibre Solutions</h3>
                        <p className="text-xs text-slate-400 font-mono">Infrastructure Transformation</p>
                      </div>
                    </div>
                    <p className="text-slate-300 text-sm leading-relaxed">
                      Transforming businesses through future-ready fiber optic infrastructure. 
                      Seamless transitions, minimal disruption, maximum performance.
                    </p>
                  </div>
      
                  {/* Services */}
                  <div>
                    <h4 className="font-semibold mb-4">Services</h4>
                    <ul className="space-y-2 text-sm text-slate-300">
                        <li><a href="/copper-to-fiber-conversion-service" className="hover:text-white transition-colors">Fibre Optics</a></li>
                      <li><a href="/network-assessment-tool" className="hover:text-white transition-colors">Technical Support</a></li>
                      <li><a href="/service-areas-local-teams" className="hover:text-white transition-colors">Emergency Support</a></li>
                      <li><a href="/network-assessment-tool" className="hover:text-white transition-colors">Maintenance Plans</a></li>
                    </ul>
                  </div>
      
                  {/* Company */}
                  <div>
                    <h4 className="font-semibold mb-4">Company</h4>
                    <ul className="space-y-2 text-sm text-slate-300">              
                      <li><a href="/about-expertise" className="hover:text-white transition-colors">About Us</a></li>
                      <li><a href="/service-areas-local-teams" className="hover:text-white transition-colors">Service Areas</a></li>
                      <li><a href="/get-quote-consultation" className="hover:text-white transition-colors">Contact Us</a></li>                
                    </ul>
                  </div>
      
                  {/* Contact */}
                  <div>
                    <h4 className="font-semibold mb-4">Contact</h4>
                    <div className="space-y-3 text-sm text-slate-300">
                      <div>
                        <p className="font-medium text-white">Emergency: 24/7</p>
                        <p>01483 343 064</p>
                      </div>
                      <div>
                        <p className="font-medium text-white">General Inquiries</p>
                        <p>admin@xtreemfibresolutions.co.uk</p>
                      </div>
                      <div>
                        <p className="font-medium text-white">Business Hours</p>
                        <p>Mon-Fri: 8AM-6PM EST</p>
                      </div>
                    </div>
                  </div>
                </div>
      
                <div className="border-t border-slate-700 mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
                  <p className="text-slate-400 text-sm">
                    © {new Date()?.getFullYear()} Xtreem Fibre Solutions. All rights reserved.
                  </p>
                  <div className="flex space-x-6 mt-4 md:mt-0">
                    {/* <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Privacy Policy</a> */}
                    <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Terms of Service</a>
                    {/* <a href="#" className="text-slate-400 hover:text-white text-sm transition-colors">Cookie Policy</a> */}
                  </div>
                </div>
              </div>
            </footer>
    </div>
  );
};

export default CopperToFiberConversionService;